import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import ContentHeader from "@/components/feature/ContentHeader";
import Footer from "@/pages/home/components/Footer";
import { track } from "@/lib/analytics";

const SIGNUP_URL = "https://app.joble.app/auth/create-account?free=true";

const VALUES = [
  {
    icon: "ri-heart-3-line",
    title: "Customers first",
    description:
      "Every decision starts with the person on the other end of the conversation. If it doesn't help them, it doesn't ship.",
  },
  {
    icon: "ri-flashlight-line",
    title: "Move fast, stay honest",
    description:
      "We ship quickly and we're straight with our customers about what works today and what's coming next.",
  },
  {
    icon: "ri-team-line",
    title: "Built by operators",
    description:
      "Our team has run shops, clinics and front desks. We build the help we always wished we had.",
  },
];

const STATS = [
  { value: "500+", label: "Businesses onboarded" },
  { value: "24/7", label: "Always-on coverage" },
  { value: "5 min", label: "Average setup time" },
];

export default function AboutPage() {
  useScrollReveal();

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "About Joble: Building the team that never sleeps";
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="relative min-h-screen bg-background-50 overflow-x-hidden">
      <ContentHeader />

      <main>
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-24 px-4 md:px-8">
          <div className="absolute -top-24 -right-24 w-[480px] h-[480px] rounded-full joble-glow-accent pointer-events-none"></div>
          <div className="relative max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-background-300/50 bg-background-100/60 px-4 py-1.5 mb-7">
              <span className="w-2 h-2 rounded-full bg-primary-400"></span>
              <span className="text-xs font-medium text-foreground-800 tracking-wide">
                About Joble
              </span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-normal text-foreground-950 tracking-tight leading-[1.1]">
              Building the team that <span className="italic font-semibold">never sleeps</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-foreground-700 leading-relaxed max-w-xl mx-auto">
              Joble started with a simple frustration: great businesses losing great customers to a
              phone that rang out and a chat box nobody was watching. We set out to make sure that
              never costs anyone a sale again.
            </p>
          </div>
        </section>

        <section className="relative bg-background-100 py-16 md:py-20 px-4 md:px-8">
          <div className="reveal max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-medium text-foreground-950 tracking-tight mb-6">
              Our mission
            </h2>
            <div className="space-y-5 text-base text-foreground-700 leading-relaxed">
              <p>
                Every missed call and every unanswered message is a customer walking away. For small
                teams, keeping up feels impossible, because you can't be on the phone, at the front
                desk, and running the business at the same time.
              </p>
              <p>
                Joble gives every business an always-on team. An AI Web Agent that answers visitors
                the moment they arrive. An AI Phone Agent that picks up on the first ring. And a
                Workspace that turns every conversation into an action, so nothing gets lost.
              </p>
              <p>
                We believe automation should feel human. It should sound like you, know your
                business, and free your team to do the work only people can do.
              </p>
            </div>
          </div>
        </section>

        <section className="relative py-16 md:py-20 px-4 md:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="reveal font-heading text-2xl sm:text-3xl md:text-4xl font-medium text-foreground-950 tracking-tight mb-12 text-center">
              What we stand for
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {VALUES.map((value) => (
                <article
                  key={value.title}
                  className="reveal rounded-2xl border border-background-300/40 bg-background-50/70 p-7 hover:border-primary-500/40 transition-colors duration-300"
                >
                  <span className="w-12 h-12 rounded-xl flex items-center justify-center bg-primary-500/12 border border-primary-500/25 mb-5">
                    <i className={`${value.icon} text-2xl text-primary-300`} aria-hidden></i>
                  </span>
                  <h3 className="text-lg font-semibold text-foreground-950 mb-2">{value.title}</h3>
                  <p className="text-sm text-foreground-700 leading-relaxed">{value.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="relative bg-background-100 py-14 md:py-16 px-4 md:px-8">
          <div className="reveal max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-8">
            {STATS.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="font-heading text-4xl md:text-5xl font-semibold text-foreground-950 tracking-tight">
                  {stat.value}
                </div>
                <div className="mt-2 text-sm text-foreground-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="relative py-20 md:py-24 px-4 md:px-8">
          <div className="reveal max-w-3xl mx-auto text-center rounded-2xl border border-primary-500/20 bg-primary-500/[0.07] p-10 md:p-14">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-medium text-foreground-950 tracking-tight">
              Ready to meet your new team?
            </h2>
            <p className="mt-4 text-base text-foreground-700 leading-relaxed max-w-lg mx-auto">
              Get set up in five minutes. No credit card, no developer, no long onboarding.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href={SIGNUP_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("try_joble_clicked", { location: "about_cta" })}
                className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 text-foreground-950 font-semibold text-base px-8 py-4 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Try Joble Now
                <i
                  className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform"
                  aria-hidden
                ></i>
              </a>
              <Link
                to="/products"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full border border-background-300/60 text-foreground-900 font-semibold text-base px-8 py-4 hover:bg-background-100/70 transition-colors cursor-pointer whitespace-nowrap"
              >
                Explore products
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}